import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {DictionaryItem, DictionaryType, UpdateDictionaryItem} from '../types/DictionaryItem';
import {useTranslatableField} from '../../../common/hooks/useTranslatableField';
import AdminFormHeader from '../../../layout/admin/AdminFormHeader';
import {AdminButton, AdminForm, AdminFormSection, AdminInputText} from '../../../layout/admin';
import FieldLanguageSwitcher from "../../../layout/admin/FieldLanguageSwitcher";
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

interface Props {
  dictionaryType: DictionaryType;
  initialValues?: DictionaryItem;
  onSubmit: (values: UpdateDictionaryItem) => Promise<void>;
  onCancel: () => void;
}

export const DictionaryItemsForm = ({
                                      dictionaryType,
                                      initialValues,
                                      onSubmit,
                                      onCancel,
                                    }: Props) => {
  const {t} = useTranslation();
  const valueField = useTranslatableField({initialValue: initialValues?.value});
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    setIsSubmitting(true);
    try {
      await onSubmit({
        value: valueField.translations,
      });
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormHeader
        title={initialValues ? t('admin.core.dictionaryItems.title.edit') : t('admin.core.dictionaryItems.title.create')}
        subtitle={initialValues ? t('admin.core.dictionaryItems.subtitle.edit') : t('admin.core.dictionaryItems.subtitle.create')}
      />

      <AdminForm
        onSubmit={handleSubmit}
        leftColumn={
          <AdminFormSection title={t('admin.common.sections.basicInfo')}>
            <AdminInputText
              label={t('admin.common.fields.value')}
              value={valueField.currentValue}
              onChange={(e) => valueField.setCurrentValue(e.target.value)}
              name="value"
              id="value"
              required={true}
              error={errors.value}
              languageSwitcher={
                <FieldLanguageSwitcher
                  currentLanguage={valueField.currentLanguage}
                  onLanguageChange={valueField.setCurrentLanguage}
                  collapsed={true}
                  translatedLanguages={valueField.translatedLanguages}
                />
              }
            />
          </AdminFormSection>
        }
        rightColumn={
          initialValues && (
            <AdminFormSection title={t('admin.common.sections.additionalInfo')}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.common.fields.alias')}
                </label>
                <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-sm text-gray-700">
                  {initialValues.alias}
                </div>
                <small className="text-gray-500 mt-1 block">
                  {t('admin.core.dictionaryItems.help.aliasGenerated')}
                </small>
              </div>
            </AdminFormSection>
          )
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={onCancel}
            />
            <AdminButton
              type="submit"
              label={initialValues ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
              disabled={isSubmitting}
            />
          </>
        }
      />
    </>
  );
};

export default DictionaryItemsForm;
